import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import webSocket from 'web-socket';
import { isFunction, isUndefined } from 'lodash';
import type { Asset } from 'reducers/current-contract';

type useDocumentPdfListenerReturnType = {
  pdfUploadStatus: Asset['status'] | null,
  resetSucceededAsyncAssetState: () => void,
};

export const useDocumentPdfListener = (agreementId: number): useDocumentPdfListenerReturnType => {
  const [pdfUploadStatus, setPdfUploadStatus] = useState<Asset['status'] | null>(null);

  const resetSucceededAsyncAssetState = useCallback(() => {
    setPdfUploadStatus(null);
  }, []);

  const subscribeToWebsocket = useCallback(() => {
    const unsubscribeToReadyWebsocket = webSocket.subscribe({
      channelName: `private-agreement-${agreementId}`,
      event: 'agreement:pdf:status:ready',
      eventCallback: (res: { status: Asset['status'] }) => {
        setPdfUploadStatus(res.status);
      },
    });

    return {
      unsubscribeToReadyWebsocket,
    };
  }, [agreementId]);

  useEffect(() => {
    if (isUndefined(agreementId) || Number.isNaN(agreementId)) {
      return () => null;
    }

    const {
      unsubscribeToReadyWebsocket,
    } = subscribeToWebsocket();

    return () => {
      if (isFunction(unsubscribeToReadyWebsocket)) {
        unsubscribeToReadyWebsocket();
      }
    };
  }, [subscribeToWebsocket, agreementId]);

  return useMemo(() => ({
    pdfUploadStatus,
    resetSucceededAsyncAssetState,
  }), [
    pdfUploadStatus,
    resetSucceededAsyncAssetState,
  ]);
};
